import React, { useCallback, useContext, useEffect, useMemo } from 'react';

import { Form } from 'react-final-form';
import { NotificationManager } from 'react-notifications';
import { useNavigate, useLocation } from 'react-router-dom';

import { FORM_ERROR } from 'final-form';

import PhoneVerificationForm from './PhoneVerificationForm';

import verificationBGImage from '../../assets/images/amazon/verificationBGImage.png';
import TenantConfig, { getTenant } from '../../config';
import { UserContext } from '../../context/UserContext';
import { formatPhoneNumber, truncateEmail } from '../../helpers/format';
import { required } from '../../helpers/validation';
import BnplEnterCode from '../Bnpl/BnplEnterCode';
import BnplFormContainer from '../Bnpl/BnplFormContainer';
import BnplLoading from '../Bnpl/BnplLoading';
import MerchantPortalFooter from '../common/MerchantPortalFooter';

const PhoneVerification = ({
  onSubmit,
  onResendCode,
  phone,
  email,
  recovery,
  otpChannel,
  title,
  className,
}) => {
  const { loading, setLoading } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!phone || phone === '+1-___-___-____') {
      navigate('/auth/sign-in');
    }
  }, [location, phone]);

  const formOptions = useMemo(() => {
    return {
      fields: new Array(6).fill('').map((_, i) => ({
        name: `code${i}`,
        validate: required("Code can't be empty"),
        required: true,
        autoFocus: i === 0,
      })),
    };
  }, []);

  const onSubmitInternal = useCallback(
    async (values) => {
      setLoading(true);
      try {
        await onSubmit(values);
        setLoading(false);
        if (recovery) {
          navigate('/auth/new-password' + location.search);
        }
      } catch (err) {
        setLoading(false);
        if (err.response.status === 500 && typeof newrelic !== 'undefined') {
          newrelic.noticeError(err);
        }
        if (err?.name === 'Pofile_Err') {
          return { [FORM_ERROR]: err };
        }
        return { [FORM_ERROR]: 'Code validation failed' };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, location, onSubmit, recovery]
  );

  const onResendCodeInternal = useCallback(
    async (e) => {
      try {
        e.preventDefault();

        await onResendCode();
        NotificationManager.success('New code has been sent!', 'Success');
      } catch (err) {
        NotificationManager.warning('Something went wrong', 'Warning');
      }
    },
    [onResendCode]
  );

  if (getTenant() === 'lightrx' && className !== 'admin') {
    return (
      <BnplFormContainer showControls={false}>
        {loading && <BnplLoading className={'bnpl-form'} isAuth />}
        <BnplEnterCode
          className="bnpl-form"
          onSubmitInternal={onSubmitInternal}
          onResendCodeInternal={onResendCodeInternal}
        />
      </BnplFormContainer>
    );
  }
  const defaultTitle =
    otpChannel === 'EMAIL' ? 'We just sent you an email' : 'We just sent you a text';

  return (
    <div className={`${className}-phone`}>
      {className === 'admin' && <img className="logo" src={TenantConfig.headerLogo} alt="logo" />}
      <div
        className={`${className} section phone-verification ${
          className !== 'admin' ? 'phone-verification-full' : ''
        }`}
      >
        <div
          className={className === 'admin' ? `${className}-phone-header` : `${className}-header`}
        >
          <h1>{title || defaultTitle}</h1>
        </div>
        <div className="subtitle">
          {otpChannel === 'EMAIL' && (
            <p>
              Please confirm your email address. We just sent a confirmation code to the email
              address registered to your account: <b>{truncateEmail(email)}</b>
            </p>
          )}
          {otpChannel !== 'EMAIL' &&
            (phone.length !== 2 ? (
              <p>
                Please confirm your phone number. We just sent a confirmation code to the phone
                number registered to your account: <b>{formatPhoneNumber(phone)}</b>
              </p>
            ) : (
              <p>
                Please confirm your phone number. We just sent a confirmation code to the phone
                number registered to your account, ending in <b>{phone}</b>
              </p>
            ))}
          <p className="meta">
            <small>Type your 6-digit security code here</small>
          </p>
        </div>
        <div className={`${className}-phone-main`}>
          <Form
            onSubmit={onSubmitInternal}
            mutators={{
              setValue: (args, state, utils) => {
                utils.changeValue(state, args[0], () => [args[1]]);
              },
            }}
            initialValues={{
              code0: '',
              code1: '',
              code2: '',
              code3: '',
              code4: '',
              code5: '',
              code: '',
            }}
            render={(props) => <PhoneVerificationForm {...props} formOptions={formOptions} />}
          />
        </div>
        {onResendCode && (
          <p className="subtitle">
            <small>
              <a href="" onClick={onResendCodeInternal}>
                Didn't receive the code? Resend code
              </a>
            </small>
          </p>
        )}
      </div>
      {TenantConfig?.auth.renderBottomImg && className !== 'admin' && (
        <img
          className="phone-verification-bg"
          src={verificationBGImage}
          alt="Verification Background Image"
        />
      )}
      {className === 'admin' && <MerchantPortalFooter />}
    </div>
  );
};

export default PhoneVerification;
